import Bugsnag from '@bugsnag/js';
import React, { ErrorInfo } from 'react';

import { getEnv } from '@/app/conf/env';
import { parseTraceResults } from '@/shared/cloudflare-trace';
import { ErrorContext, getErrorDetails } from '@/shared/errors';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback: (props: { error: ErrorContext; resetErrorBoundary: () => void }) => React.ReactNode;
};

type ErrorBoundaryState = {
  error: ErrorContext | null;
};

export default class AppErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      error: null
    };
  }

  async componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error } as any);

    const errorContext = getErrorDetails(error, info);

    const cloudflareTraceUrl = getEnv().CLOUDFLARE_TRACE_URL;
    let traceStatus = 'disabled';
    let traceResults = {};

    if (cloudflareTraceUrl) {
      try {
        const response = await fetch(cloudflareTraceUrl);
        const text = await response.text();

        traceResults = parseTraceResults(text);
        traceStatus = 'success';
      } catch (ex: any) {
        traceStatus = 'failed';
        traceResults = {
          message: ex.message
        };
      }
    }

    if (errorContext.report) {
      Bugsnag.notify(error, (event) => {
        event.addMetadata('react', info);
        event.addMetadata('cloudflare', { status: traceStatus, ...traceResults });
      });

      console.warn('Reported to Bugsnag');
    }
  }

  handleClearError() {
    this.setState({});
  }

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      return fallback({
        error: error,
        resetErrorBoundary: () => this.handleClearError
      });
    }

    return children ?? null;
  }
}
